import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import type { ApiError, ErrorCode } from "@/store/apis/adminApi.types";

/**
 * Errores que puede devolver RTK Query en `mutation.unwrap().catch(err)`
 * o en el `.error` de un query result.
 */
export type RtkError = FetchBaseQueryError | SerializedError | undefined;

function isFetchBaseQueryError(err: unknown): err is FetchBaseQueryError {
  return typeof err === "object" && err !== null && "status" in err;
}

function isSerializedError(err: unknown): err is SerializedError {
  return (
    typeof err === "object" &&
    err !== null &&
    "message" in err &&
    typeof (err as { message?: unknown }).message === "string" &&
    !("status" in err)
  );
}

function isApiError(body: unknown): body is ApiError {
  return typeof body === "object" && body !== null && "code" in body && "message" in body;
}

/**
 * Extrae un mensaje legible del error para mostrar en toasts / cards.
 * Prioridad: mensaje del backend (`ErrorResponse`) → mensaje del
 * transport (fetch/network) → fallback.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Ocurrió un error inesperado",
): string {
  if (!error) return fallback;
  if (isFetchBaseQueryError(error)) {
    const body = error.data;
    if (isApiError(body)) return body.message;
    if (typeof body === "string" && body.length > 0) return body;
    if (typeof error.status === "number") return `Error ${error.status}`;
    if (typeof error.status === "string") return error.status;
  }
  if (isSerializedError(error) && error.message) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

/**
 * Extrae el `code` del ErrorResponse (ej. "ERR_MFA_REQUIRED",
 * "ERR_RATE_LIMITED") para routing/logic condicional.
 */
export function getApiErrorCode(error: unknown): ErrorCode | undefined {
  if (!isFetchBaseQueryError(error)) return undefined;
  const body = error.data;
  if (isApiError(body)) return body.code as ErrorCode;
  return undefined;
}

/**
 * Extrae el `field` del ErrorResponse — el back lo rellena cuando sabe
 * qué input causó el conflicto/validación (ej. `position` en consejos
 * duplicados, `slug` en categorías repetidas).
 *
 * Útil para anclar el error al input específico con
 * `form.setError(field, { message })` en vez de mostrar un alert
 * genérico al pie del form. Si el back no identifica el campo, devuelve
 * `undefined` y el llamador debe caer al InlineError genérico.
 */
export function getApiErrorField(error: unknown): string | undefined {
  if (!isFetchBaseQueryError(error)) return undefined;
  const body = error.data;
  if (!isApiError(body)) return undefined;
  const field = (body as { field?: unknown }).field;
  return typeof field === "string" && field.length > 0 ? field : undefined;
}
